import {
  cleanUpStationList,
  ErrorInfo,
  getUserUsername,
  selectUserEmail,
  useApiClient,
  useDashboardConfigSync,
  useForecastPreferenceSync,
  useMyWeatherOrderSync,
  useMyWeatherSync,
  usePublicConfigRefresh,
  UserService,
  useUserEdit,
  useUserLoggedIn,
  useEffectOnce,
  useUpdateEffect,
} from '@smartmixin/core';
import {StatusCodes} from 'http-status-codes';
import React, {PropsWithChildren} from 'react';
import ReactGA from 'react-ga4';
import {useDispatch, useSelector} from 'react-redux';
import {SentryUtils} from '.';
import {useWindowFocus} from '../hooks';

const UserSyncProvider = ({children}: PropsWithChildren) => {
  const dispatch = useDispatch();
  const {fetch} = useApiClient();
  const editUserDetails = useUserEdit();
  const isLoggedIn = useUserLoggedIn();
  // const purchase = useSelector(selectPurchase);
  const email = useSelector(selectUserEmail);
  const username = useSelector(getUserUsername);
  const isFocused = useWindowFocus();

  // Sync per-station dashboard layout changes up to the backend (logged-in
  // only; anonymous config stays local).
  useDashboardConfigSync();
  // Same, for the device-shared forecast model preference. The matching pull
  // is the window-focus refresh below.
  useForecastPreferenceSync();
  // Uploads this device's places and signed-out station favourites to the
  // account (backend#266); off until the My weather sync is switched on.
  useMyWeatherSync();
  // Sends a My weather order reordered on this device (FE #381).
  useMyWeatherOrderSync();
  // `GET /api/app/flags` (#379): public flags, their rollouts and versions,
  // signed in or out. Silent, and a failure keeps what is stored (core).
  const refreshPublicConfig = usePublicConfigRefresh();

  /***************************************
   * INITIAL SYNC WITH SMARTMIXIN SERVERS
   ****************************************/
  useEffectOnce(() => {
    refreshPublicConfig();
    dispatch(cleanUpStationList());
    if (isLoggedIn) {
      // authenticated, sync user details
      fetch(UserService.getUserDetail())
        .then((data) => {
          editUserDetails(data);
        })
        .catch((err: ErrorInfo) => {
          if (err.statusCode === StatusCodes.UNAUTHORIZED) {
            // Account seems to be not valid anymore
            dispatch({type: 'LOGOUT'});
          }
        });
    }
    // else if (purchase) {
    //   // For unauthenticated users, check subscription validity
    //   fetch(BillingService.getPurchaseStatus(purchase.id), { ignore404: true })
    //     .then((data: BillingStatus) => {
    //       dispatch(setBillingStatus(data));
    //     })
    //     .catch(() => {});
    // }
  });

  /***************************************
   * APP STATE MONITORING
   ****************************************/
  // Both seeded from the same initial focus, exactly as mobile seeds both from
  // AppState.currentState. `previousAppState` used to start at null with its
  // only setter behind an `!== null` guard, so it could never leave null and
  // this entire refresh was dead code -- a preference changed on another
  // device was never observed by an open tab (#178).
  const [currentAppState, setCurrentAppState] = React.useState(isFocused);
  const [previousAppState, setPreviousAppState] = React.useState(isFocused);

  useUpdateEffect(() => {
    setCurrentAppState(isFocused);
  }, [isFocused]);

  useUpdateEffect(() => {
    if (currentAppState !== previousAppState) {
      if (currentAppState) {
        refreshPublicConfig({debounce: 5000});
        if (isLoggedIn) {
          fetch(UserService.getUserDetail(), {debounce: 5000})
            .then((data) => {
              editUserDetails(data);
            })
            .catch((err: ErrorInfo) => {
              if (err.statusCode === StatusCodes.UNAUTHORIZED) {
                // Account seems to be not valid anymore
                dispatch({type: 'LOGOUT'});
              }
            });
        }
      }
      setPreviousAppState(currentAppState);
    }
  }, [
    currentAppState,
    previousAppState,
    isLoggedIn,
    fetch,
    editUserDetails,
    refreshPublicConfig,
  ]);

  React.useEffect(() => {
    // Older releases used the email address as GA User-ID. Email is PII and
    // must never be sent to Analytics, so clear that value for every session.
    ReactGA.set({userId: null});
    if (isLoggedIn) {
      if (email) {
        SentryUtils.setUser(email, username);
      }
    }
  }, [isLoggedIn, email, username]);

  // The alert snackbar is the shell's (AppContainer): it sits above the
  // bottom bar, whose height only the shell knows.
  return <>{children}</>;
};

export default UserSyncProvider;
